import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GetRowIdParams, RowSelectedEvent } from 'ag-grid-enterprise';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  ClientRegisteredVendor,
  CompanyComplianceType
} from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import {
  ApConfigService,
  buildApConfigSelectionMap
} from 'src/app/services/ap-config.service';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import {
  ApVendorsMapToClientRegisteredVendorResponseDto,
  ClientRegisteredVendorsCreateRequestDto,
  CompanyComplianceTypeDto,
  CompanyDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import { AddEditClientRegisteredVendorModalComponent } from '../add-edit-client-registered-vendor-modal/add-edit-client-registered-vendor-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

declare type ClientRegisteredVendorUpload =
  ClientRegisteredVendorsCreateRequestDto &
    Pick<
      ApVendorsMapToClientRegisteredVendorResponseDto,
      'ApVendors' | 'missingRequiredData' | 'hasDuplicateInfo'
    > & {
      CompanyVendors: (ApVendorsMapToClientRegisteredVendorResponseDto['CompanyVendors'][0] & {
        forceTempActive?: boolean;
        forceTempActiveTime?: number;
      })[];
      CompanyComplianceTypeId: number;
      forceTempActiveTime: number;
      forceActiveCounter: number;
      hasSharedTaxId?: boolean;
      taxId?: string;
      rowId?: string;
    };

@Component({
  selector: 'app-client-registered-vendors-upload-modal',
  templateUrl: './client-registered-vendors-upload-modal.component.html',
  styleUrl: './client-registered-vendors-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientRegisteredVendorsUploadModalComponent
  implements OnInit, AfterViewInit
{
  constructor(
    private activeModal: NgbActiveModal,
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private HostElement: ElementRef,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private ApConfigService: ApConfigService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  @Input() ClientRegisteredVendors?: ClientRegisteredVendorUpload[];
  @Input() CompanyId?: number;
  @Input() country: 'US' | 'CA' = 'US';
  @Input() selectedApConfigIds?: number[];

  title: string = 'Client Registered Vendors Upload';
  duplicateVendorCount: number = 0;
  missingRequiredFieldVendorCount: number = 0;
  sharedTaxIdVendorCount: number = 0;
  Company?: CompanyDto;
  CompanyComplianceTypes: CompanyComplianceTypeDto[] = [];
  selectedCompanyComplianceType?: CompanyComplianceType;
  selectedRows: ClientRegisteredVendorUpload[] = [];

  // VPDW-763: batch-level accounting-system designation for the whole upload. The
  // chosen system(s) apply to every vendor in the file; empty = no AP creation
  // (unchanged behavior). Only shown when the client has create-eligible systems.
  apConfigSelectionMap: Map<string, number> = new Map();
  selectedApConfigIdsMap: Map<string, number> = new Map();

  private readonly taxIdFields = ['ein', 'ssn', 'bsn'] as const;

  private rowDataSubject = new BehaviorSubject<any[]>([]);
  rowData: Observable<any[]> = this.rowDataSubject.asObservable();
  rowClassRules = {
    'gridRow-yellow': (params: any) => params.data.hasDuplicateInfo === true,
    'gridRow-red': (params: any) => params.data.missingRequiredData === true,
    'gridRow-pink': (params: any) => params.data.hasSharedTaxId === true
  };
  reloadGrid: boolean = false;
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'delete']
  };

  getVendorRowId = (params: GetRowIdParams) => String(params.data['rowId']);

  close(reason?: string) {
    this.activeModal.close(reason);
  }

  rowSelectionChanged(event: RowSelectedEvent) {
    const selectedRows = event.api.getSelectedRows();
    this.selectedRows.splice(0, this.selectedRows.length, ...selectedRows);
  }

  apConfigSelectionChange(selectedItems: Map<string, number>): void {
    this.selectedApConfigIdsMap = selectedItems;
  }

  private loadCreateEligibleApConfigs(): void {
    this.ApConfigService.getCreateEligibleApConfigs(this.CompanyId).subscribe({
      next: (apConfigs) => {
        this.apConfigSelectionMap = buildApConfigSelectionMap(apConfigs);

        if (this.selectedApConfigIds?.length) {
          const selectedIds = new Set(this.selectedApConfigIds);
          this.apConfigSelectionMap = new Map(
            Array.from(this.apConfigSelectionMap.entries()).filter(([, id]) =>
              selectedIds.has(id)
            )
          );
          this.selectedApConfigIdsMap = new Map(this.apConfigSelectionMap);
        }

        // Zoneless: flush the async result to the view.
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  upload() {
    if (!this.ClientRegisteredVendors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload clientRegisteredVendors. There are no clientRegisteredVendors to upload.',
        { returnError: true }
      );
    }

    if (this.ClientRegisteredVendors.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload clientRegisteredVendors. There are no clientRegisteredVendors to upload.',
        { returnError: true }
      );
    }

    if (this.missingRequiredFieldVendorCount > 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload clientRegisteredVendors. Some vendors are missing required data.',
        { returnError: true }
      );
    }

    if (this.duplicateVendorCount > 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload clientRegisteredVendors. Duplicate vendor data found.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing client ID.', {
        returnError: true
      });
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Uploading clientRegisteredVendors...'
    });

    this.ClientRegisteredVendors = this.ClientRegisteredVendors.map((crv) => {
      const { CompanyVendors } = crv;

      if (!CompanyVendors || (CompanyVendors && CompanyVendors.length !== 1)) {
        mask.destroy();
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot upload clientRegisteredVendors. Missing companyVendor record.',
          { returnError: true }
        );
      }

      const { CompanyComplianceType, forceTempActive, forceTempActiveTime } =
        CompanyVendors[0];

      if (
        !CompanyComplianceType ||
        (CompanyComplianceType && !CompanyComplianceType.id)
      ) {
        mask.destroy();
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot upload clientRegisteredVendors. Missing Company Compliance type.',
          { returnError: true }
        );
      }

      return Object.assign(crv, {
        CompanyComplianceTypeId: CompanyComplianceType.id,
        CompanyId: this.CompanyId,
        ...(forceTempActive !== undefined && { forceTempActive }),
        ...(forceTempActiveTime !== undefined && { forceTempActiveTime })
      });
    });

    const selectedApConfigIds = Array.from(
      this.selectedApConfigIdsMap.values()
    );

    if (
      this.Company?.allowAutomatedBusinessVerificationClientRegisteredVendors
    ) {
      this.ClientRegisteredVendorService.saveClientRegisteredVendorsHandleCompliance(
        this.ClientRegisteredVendors as ClientRegisteredVendor[],
        {
          sendEmail: true,
          // VPDW-763: only create AP vendors when the admin designated system(s);
          // otherwise bulk upload stays create-free (unchanged behavior).
          ...(selectedApConfigIds.length
            ? { createApVendor: true, apConfigIds: selectedApConfigIds }
            : {})
        }
      ).subscribe({
        next: () => {
          mask.destroy();
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully saved clientRegisteredVendors.'
          );
          this.close();
        },
        error: () => {
          mask.destroy();
          this.close();
        }
      });
    } else {
      from(
        companyVendorsService.createManyNewClientRegisteredVendors({
          pathParams: { companyId: this.CompanyId! },
          options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS },
          data: {
            items: this.ClientRegisteredVendors.map(
              (clientRegisteredVendor) => {
                const { ApVendors, CompanyComplianceTypeId } =
                  clientRegisteredVendor;

                const apVendorId =
                  ApVendors && ApVendors.length === 1 && ApVendors[0].id;

                return {
                  ...(selectedApConfigIds &&
                    selectedApConfigIds.length > 0 && {
                      apConfigIds: selectedApConfigIds
                    }),
                  ...(apVendorId && { apVendorIdToLink: apVendorId }),
                  CompanyComplianceTypeId,
                  forceTempActiveTime:
                    clientRegisteredVendor.forceTempActiveTime,
                  forceActiveCounter: clientRegisteredVendor.forceActiveCounter,
                  clientRegisteredVendor: {
                    accountingId: clientRegisteredVendor.accountingId ?? null,
                    dba: clientRegisteredVendor.dba ?? null,
                    ein: clientRegisteredVendor.ein ?? null,
                    ssn: clientRegisteredVendor.ssn ?? null,
                    bsn: clientRegisteredVendor.bsn ?? null,
                    taxIdentification:
                      clientRegisteredVendor.taxIdentification ?? null,
                    phone: clientRegisteredVendor.phone ?? null,
                    phone2: clientRegisteredVendor.phone2 ?? null,
                    fax: clientRegisteredVendor.fax ?? null,
                    legalName: clientRegisteredVendor.legalName ?? '',
                    ...(clientRegisteredVendor.companyType && {
                      companyType: clientRegisteredVendor.companyType
                    }),
                    ...(clientRegisteredVendor.jurisdiction && {
                      jurisdiction: clientRegisteredVendor.jurisdiction
                    }),
                    ...(clientRegisteredVendor.llcType && {
                      llcType: clientRegisteredVendor.llcType
                    }),
                    ...(clientRegisteredVendor.requesterFirstName && {
                      requesterFirstName:
                        clientRegisteredVendor.requesterFirstName
                    }),
                    ...(clientRegisteredVendor.requesterLastName && {
                      requesterLastName:
                        clientRegisteredVendor.requesterLastName
                    }),
                    ...(clientRegisteredVendor.isSoleProprietorship !==
                      undefined && {
                      isSoleProprietorship:
                        clientRegisteredVendor.isSoleProprietorship
                    }),
                    ...(clientRegisteredVendor.isTakeOver !== undefined && {
                      isTakeOver: clientRegisteredVendor.isTakeOver
                    }),
                    ...(clientRegisteredVendor.isOffsite !== undefined && {
                      isOffsite: clientRegisteredVendor.isOffsite
                    }),
                    ...(clientRegisteredVendor.isOnSite !== undefined && {
                      isOnSite: clientRegisteredVendor.isOnSite
                    }),
                    ...(clientRegisteredVendor.isSupplier !== undefined && {
                      isSupplier: clientRegisteredVendor.isSupplier
                    }),
                    ...(clientRegisteredVendor.website && {
                      website: clientRegisteredVendor.website
                    }),
                    ...(clientRegisteredVendor.StreetAddress && {
                      StreetAddress: {
                        address1:
                          clientRegisteredVendor.StreetAddress.address1 ?? null,
                        address2:
                          clientRegisteredVendor.StreetAddress.address2 ?? null,
                        city: clientRegisteredVendor.StreetAddress.city ?? null,
                        state:
                          clientRegisteredVendor.StreetAddress.state ?? null,
                        zip: clientRegisteredVendor.StreetAddress.zip ?? null,
                        country:
                          clientRegisteredVendor.StreetAddress.country ?? null
                      }
                    }),
                    ...(clientRegisteredVendor.MailingAddress && {
                      MailingAddress: {
                        address1:
                          clientRegisteredVendor.MailingAddress.address1 ??
                          null,
                        address2:
                          clientRegisteredVendor.MailingAddress.address2 ??
                          null,
                        city:
                          clientRegisteredVendor.MailingAddress.city ?? null,
                        state:
                          clientRegisteredVendor.MailingAddress.state ?? null,
                        zip: clientRegisteredVendor.MailingAddress.zip ?? null,
                        country:
                          clientRegisteredVendor.MailingAddress.country ?? null
                      }
                    }),
                    Contacts: (clientRegisteredVendor.Contacts ?? []).map(
                      (contact) => {
                        const {
                          email,
                          firstName,
                          lastName,
                          jobTitle,
                          phone1,
                          phone2,
                          ext,
                          ext2,
                          isCompliance,
                          isPrimaryCompliance
                        } = contact;

                        return {
                          ...(email && { email }),
                          ...(firstName && { firstName }),
                          ...(lastName && { lastName }),
                          ...(jobTitle && { jobTitle }),
                          ...(phone1 && { phone1 }),
                          ...(phone2 && { phone2 }),
                          ...(ext && { ext }),
                          ...(ext2 && { ext2 }),
                          ...(isCompliance !== undefined && { isCompliance }),
                          ...(isPrimaryCompliance !== undefined && {
                            isPrimaryCompliance
                          })
                        };
                      }
                    )
                  }
                };
              }
            )
          }
        })
      ).subscribe({
        next: () => {
          mask.destroy();
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully saved clientRegisteredVendors.'
          );
          this.close();
        },
        error: () => {
          mask.destroy();
          this.close();
        }
      });
    }
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'edit') {
      const { CompanyVendors } = rowData;
      if (!CompanyVendors || (CompanyVendors && CompanyVendors.length === 0)) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot edit vendor. Missing CompanyVendor Record.',
          { returnError: true }
        );
      }

      const CompanyVendor = CompanyVendors[0];
      const { CompanyComplianceType } = CompanyVendor;

      if (!CompanyComplianceType) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot edit vendor. Missing Company Compliance type.',
          { returnError: true }
        );
      }

      if (!this.CompanyId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot edit vendor. Missing Company ID.',
          { returnError: true }
        );
      }

      const modalRef = this.ModalService.open(
        AddEditClientRegisteredVendorModalComponent,
        { centered: true, size: 'lg' }
      );
      Object.assign(modalRef.componentInstance, {
        ClientRegisteredVendor: rowData,
        CompanyId: this.CompanyId,
        CompanyComplianceType: CompanyComplianceType,
        country: this.country
      });

      modalRef.closed.subscribe({
        next: (ClientRegisteredVendor) => {
          Object.assign(rowData, ClientRegisteredVendor);

          this.reloadGrid = true;
          this.calculateCounts(this.ClientRegisteredVendors!);
        }
      });
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );
      Object.assign(componentInstance, {
        displayText:
          'Are you sure you want to remove this vendor from the upload?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            const index = this.ClientRegisteredVendors?.findIndex(
              (vendor) => vendor.legalName === rowData.legalName
            );

            if (index !== -1 && index !== undefined) {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully removed vendor from upload.'
              );
              this.ClientRegisteredVendors?.splice(index!, 1);
              this.rowDataSubject.next([...this.ClientRegisteredVendors!]);
              this.calculateCounts(this.ClientRegisteredVendors!);
            } else {
              this.ToastService.addErrorToast(
                'Error',
                'No vendor found to remove.'
              );
            }
          }
        }
      });
    }
  }

  applyComplianceTypeToSelectedRows(): void {
    if (!this.selectedCompanyComplianceType) {
      this.ToastService.addErrorToast(
        'Error',
        'Please select a compliance type before applying it to the selected rows.'
      );
      return;
    }

    for (const row of this.selectedRows) {
      for (const companyVendor of row.CompanyVendors ?? []) {
        Object.assign(companyVendor, {
          CompanyComplianceType: this.selectedCompanyComplianceType
        });
      }

      row.CompanyComplianceTypeId = this.selectedCompanyComplianceType.id!;
    }

    this.reloadGrid = true;

    this.ToastService.addSuccessToast(
      'Success',
      `Successfully applied changes to ${this.selectedRows.length} vendor${this.selectedRows.length !== 1 ? 's' : ''}.`
    );
  }

  removeRedRows() {
    if (!this.ClientRegisteredVendors) {
      return;
    }

    const originalCount = this.ClientRegisteredVendors.length;
    this.ClientRegisteredVendors = this.ClientRegisteredVendors.filter(
      (vendor: any) => !vendor.missingRequiredData
    );

    const removedCount = originalCount - this.ClientRegisteredVendors.length;
    if (removedCount > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Successfully removed ${removedCount} vendor${removedCount !== 1 ? 's' : ''} with missing required data.`
      );
      this.rowDataSubject.next([...this.ClientRegisteredVendors]);
      this.calculateCounts(this.ClientRegisteredVendors);
    }
  }

  removeYellowRows() {
    if (!this.ClientRegisteredVendors) {
      return;
    }

    const originalCount = this.ClientRegisteredVendors.length;
    this.ClientRegisteredVendors = this.ClientRegisteredVendors.filter(
      (vendor: any) => !vendor.hasDuplicateInfo
    );

    const removedCount = originalCount - this.ClientRegisteredVendors.length;
    if (removedCount > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Successfully removed ${removedCount} duplicate vendor${removedCount !== 1 ? 's' : ''}.`
      );
      this.rowDataSubject.next([...this.ClientRegisteredVendors]);
      this.calculateCounts(this.ClientRegisteredVendors);
    }
  }

  removePinkRows() {
    if (!this.ClientRegisteredVendors) {
      return;
    }

    const originalCount = this.ClientRegisteredVendors.length;
    this.ClientRegisteredVendors = this.ClientRegisteredVendors.filter(
      (vendor) => !vendor.hasSharedTaxId
    );

    const removedCount = originalCount - this.ClientRegisteredVendors.length;
    if (removedCount > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Successfully removed ${removedCount} vendor${removedCount !== 1 ? 's' : ''} sharing a tax id with another vendor in the upload.`
      );
      this.rowDataSubject.next([...this.ClientRegisteredVendors]);
      this.calculateCounts(this.ClientRegisteredVendors);
    }
  }

  calculateCounts(rows: any[]) {
    if (!rows) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot calculate counts. No ClientRegisteredVendor data found.',
        { returnError: true }
      );
    }

    this.duplicateVendorCount = rows.filter(
      (row: any) => row.hasDuplicateInfo
    ).length;
    this.missingRequiredFieldVendorCount = rows.filter(
      (row: any) => row.missingRequiredData
    ).length;

    this.sharedTaxIdVendorCount = this.markSharedTaxIdRows(
      rows as ClientRegisteredVendorUpload[]
    );
  }

  private markSharedTaxIdRows(rows: ClientRegisteredVendorUpload[]): number {
    const taxIdCounts = {
      ein: new Map<string, number>(),
      ssn: new Map<string, number>(),
      bsn: new Map<string, number>()
    };

    for (const row of rows) {
      for (const field of this.taxIdFields) {
        const taxId = this.stripTaxId(row[field]);

        if (taxId) {
          taxIdCounts[field].set(
            taxId,
            (taxIdCounts[field].get(taxId) || 0) + 1
          );
        }
      }
    }

    let sharedTaxIdRowCount = 0;

    for (const row of rows) {
      row.hasSharedTaxId = this.taxIdFields.some((field) => {
        const taxId = this.stripTaxId(row[field]);

        return !!taxId && (taxIdCounts[field].get(taxId) || 0) > 1;
      });

      if (row.hasSharedTaxId) {
        sharedTaxIdRowCount++;
      }
    }

    return sharedTaxIdRowCount;
  }

  private stripTaxId(taxId?: string | null): string {
    return taxId ? taxId.replace(/[^0-9]/g, '') : '';
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      this.activeModal.close();
      setTimeout(() => {
        this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
          returnError: true
        });
        this.ChangeDetectorRef.detectChanges();
      });

      this.rowDataSubject.next([]);
      return;
    }

    if (
      !this.ClientRegisteredVendors ||
      this.ClientRegisteredVendors.length === 0
    ) {
      this.activeModal.close();
      setTimeout(() => {
        this.ToastService.addErrorToast(
          'Error',
          'Cannot upload ClientRegisteredVendors. No ClientRegisteredVendors found.'
        );
        this.ChangeDetectorRef.detectChanges();
      });
      this.rowDataSubject.next([]);
      return;
    }

    this.ClientRegisteredVendors = this.ClientRegisteredVendors.map((crv) => ({
      ...crv,
      taxId: crv.ein || crv.ssn || crv.bsn || '',
      rowId: crypto.randomUUID()
    }));

    from(
      companiesService.findOneById({
        pathParams: { companyId: this.CompanyId },
        queryParams: {
          includeAssociations: [
            {
              name: 'CompanyComplianceTypes'
            }
          ]
        }
      })
    ).subscribe({
      next: (Company) => {
        const { CompanyComplianceTypes } = Company;
        this.CompanyComplianceTypes.push(...(CompanyComplianceTypes ?? []));
        this.Company = Company;
        this.rowDataSubject.next([...this.ClientRegisteredVendors!]);
        this.calculateCounts(this.ClientRegisteredVendors || []);
        this.loadCreateEligibleApConfigs();
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.ChangeDetectorRef.detectChanges();
    }, 150);
  }
}
